import React, { useState, useEffect, useMemo } from 'react';
import { Form as BootstrapForm, FilterGrid, FilterField } from '@/components/ui';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import FiltersCard from '../../../components/FiltersCard';
import type { WorkIdFilterOptions, WorkIdListFilters, WorkIdDateFilterType } from '../workIdService';
import { WORK_ID_STATUS_OPTIONS } from '../utils/workIdStatusConstants';
import { csvToValues, valuesToCsv } from '../utils/workIdQueryHelpers';
import { ASSET_TYPE_FILTER_OPTIONS } from '../../../constants/assetTypes';

type ValueLabelOption = { value: string; label: string };

const PRIORITY_OPTIONS: ValueLabelOption[] = [
  { value: 'High', label: 'High' },
  { value: 'Medium', label: 'Medium' },
  { value: 'Low', label: 'Low' }
];

const DATE_FILTER_TYPE_OPTIONS: { value: WorkIdDateFilterType; label: string }[] = [
  { value: 'reportedOn', label: 'Reported date' },
  { value: 'taskCompletion', label: 'Completion date (from task)' }
];

type SourceDropdownRow = { key: string; label: string };

/** Filter card edits every list filter except free-text search (handled in page search bar). */
export type WorkIdFiltersApplyPayload = Omit<WorkIdListFilters, 'search'>;

/** Multiselect filters: comma-separated string in state, array in the dropdown. */
type MultiFilterKey = 'serviceAreaId' | 'foremanAreaId' | 'assetType' | 'priority' | 'reportedById' | 'workSource';

interface WorkIdFiltersProps {
  filterOptions: WorkIdFilterOptions;
  filters: WorkIdListFilters;
  onClearFilters: () => void;
  onApplyFilters: (filters: WorkIdFiltersApplyPayload) => void;
}

function normalizePending(f: WorkIdListFilters): WorkIdFiltersApplyPayload {
  return {
    serviceAreaId: f.serviceAreaId ?? '',
    foremanAreaId: f.foremanAreaId ?? '',
    assetType: f.assetType ?? '',
    status: f.status,
    priority: f.priority ?? '',
    dateFrom: f.dateFrom ?? '',
    dateTo: f.dateTo ?? '',
    dateFilterType: f.dateFilterType === 'taskCompletion' ? 'taskCompletion' : 'reportedOn',
    reportedById: f.reportedById ?? '',
    workSource: f.workSource ?? ''
  };
}

const WorkIdFilters: React.FC<WorkIdFiltersProps> = ({
  filterOptions,
  filters,
  onClearFilters,
  onApplyFilters
}) => {
  const [pendingFilters, setPendingFilters] = useState<WorkIdFiltersApplyPayload>(() =>
    normalizePending(filters)
  );

  useEffect(() => {
    setPendingFilters(normalizePending(filters));
  }, [filters]);

  const filteredForemanAreas = useMemo(() => {
    const selectedServiceAreaIds = csvToValues(pendingFilters.serviceAreaId);
    const areas =
      selectedServiceAreaIds.length > 0
        ? filterOptions.foremanAreas.filter((area) =>
            selectedServiceAreaIds.includes(String(area.serviceAreaId))
          )
        : filterOptions.foremanAreas;
    return [...areas].sort((a, b) => a.name.localeCompare(b.name));
  }, [filterOptions.foremanAreas, pendingFilters.serviceAreaId]);

  const sourceOptions: SourceDropdownRow[] = useMemo(
    () =>
      filterOptions.sources.map((s) => ({
        key: s.kind === 'form' ? `form|${s.formId}` : `name|${encodeURIComponent(s.formName)}`,
        label: s.label
      })),
    [filterOptions.sources]
  );

  const handlePendingFilterChange = (name: keyof WorkIdFiltersApplyPayload, value: string) => {
    setPendingFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleMultiFilterChange = (name: MultiFilterKey, values: (string | number)[]) => {
    const csv = valuesToCsv(values);
    setPendingFilters((prev) => {
      const next = { ...prev, [name]: csv };
      if (name === 'serviceAreaId' && csv !== prev.serviceAreaId) {
        // Keep only the foreman areas that still belong to a selected service area.
        const selectedServiceAreaIds = csvToValues(csv);
        if (selectedServiceAreaIds.length > 0) {
          const allowedForemanAreaIds = new Set(
            filterOptions.foremanAreas
              .filter((area) => selectedServiceAreaIds.includes(String(area.serviceAreaId)))
              .map((area) => String(area.id))
          );
          next.foremanAreaId = valuesToCsv(
            csvToValues(prev.foremanAreaId).filter((id) => allowedForemanAreaIds.has(id))
          );
        }
      }
      return next;
    });
  };

  const handleDateFilterTypeChange = (value: string) => {
    if (value === 'reportedOn' || value === 'taskCompletion') {
      setPendingFilters((prev) => ({ ...prev, dateFilterType: value }));
    }
  };

  const handleStatusChange = (values: (string | number)[]) => {
    const nextStatus = valuesToCsv(values) || 'exclude-cancelled';
    setPendingFilters((prev) => ({ ...prev, status: nextStatus }));
  };

  const applied = normalizePending(filters);

  const hasActiveFilters = () =>
    applied.serviceAreaId !== '' ||
    applied.foremanAreaId !== '' ||
    applied.assetType !== '' ||
    (applied.status !== '' && applied.status !== 'exclude-cancelled') ||
    (applied.priority !== '' && applied.priority != null) ||
    (applied.dateFrom ?? '') !== '' ||
    (applied.dateTo ?? '') !== '' ||
    (applied.reportedById ?? '') !== '' ||
    (applied.workSource ?? '') !== '';

  const hasUnappliedChanges = () =>
    pendingFilters.serviceAreaId !== applied.serviceAreaId ||
    pendingFilters.foremanAreaId !== applied.foremanAreaId ||
    pendingFilters.assetType !== applied.assetType ||
    pendingFilters.status !== applied.status ||
    pendingFilters.priority !== applied.priority ||
    (pendingFilters.dateFrom ?? '') !== (applied.dateFrom ?? '') ||
    (pendingFilters.dateTo ?? '') !== (applied.dateTo ?? '') ||
    (pendingFilters.dateFilterType ?? 'reportedOn') !== (applied.dateFilterType ?? 'reportedOn') ||
    (pendingFilters.reportedById ?? '') !== (applied.reportedById ?? '') ||
    (pendingFilters.workSource ?? '') !== (applied.workSource ?? '');

  const handleApplyFiltersClick = () => {
    onApplyFilters(pendingFilters);
  };

  const handleClearFilters = () => {
    const empty: WorkIdFiltersApplyPayload = {
      serviceAreaId: '',
      foremanAreaId: '',
      assetType: '',
      status: 'exclude-cancelled',
      priority: '',
      dateFrom: '',
      dateTo: '',
      dateFilterType: 'reportedOn',
      reportedById: '',
      workSource: ''
    };
    setPendingFilters(empty);
    onClearFilters();
  };

  return (
    <FiltersCard
      title="Work ID Filters"
      subtitle="Filter by area, asset type, status, dates, priority, reporter, and source. Default view excludes CANCELLED, MERGED, ASSIGNED, CLOSED, and CLOSED_WITHOUT_ACTION. Search can find hidden statuses."
      collapsible={true}
      defaultCollapsed={false}
      showApplyButton={true}
      onApply={handleApplyFiltersClick}
      applyDisabled={!hasUnappliedChanges()}
      showClearButton={true}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters()}
    >
      <BootstrapForm>
        <FilterGrid>
          <FilterField>
            <MultiSelectDropdown<WorkIdFilterOptions['serviceAreas'][0]>
              options={filterOptions.serviceAreas}
              value={csvToValues(pendingFilters.serviceAreaId)}
              onChange={(values) => handleMultiFilterChange('serviceAreaId', values)}
              getValue={(area) => area.id.toString()}
              formatOption={(area) => area.name}
              placeholder="All Service Areas"
              className="tw-w-full"
              label="Service Area"
              noOptionsMessage="No service areas available"
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<WorkIdFilterOptions['foremanAreas'][0]>
              options={filteredForemanAreas}
              value={csvToValues(pendingFilters.foremanAreaId)}
              onChange={(values) => handleMultiFilterChange('foremanAreaId', values)}
              getValue={(area) => area.id.toString()}
              formatOption={(area) => area.name}
              placeholder="All Foreman Areas"
              className="tw-w-full"
              label="Foreman Area"
              noOptionsMessage="No foreman areas available for selected filters"
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<ValueLabelOption>
              options={ASSET_TYPE_FILTER_OPTIONS}
              value={csvToValues(pendingFilters.assetType)}
              onChange={(values) => handleMultiFilterChange('assetType', values)}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="All asset types"
              className="tw-w-full"
              label="Asset Type"
              noOptionsMessage="No asset types available"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<ValueLabelOption>
              options={WORK_ID_STATUS_OPTIONS}
              value={
                pendingFilters.status === 'exclude-cancelled' || !pendingFilters.status
                  ? []
                  : csvToValues(pendingFilters.status)
              }
              onChange={handleStatusChange}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="Default"
              className="tw-w-full"
              label="Status"
              noOptionsMessage="No status options available"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>

          <FilterField>
            <BootstrapForm.Group>
              <BootstrapForm.Label>Start date</BootstrapForm.Label>
              <BootstrapForm.Control
                type="date"
                size="sm"
                value={pendingFilters.dateFrom ?? ''}
                onChange={(e) => handlePendingFilterChange('dateFrom', e.target.value)}
              />
            </BootstrapForm.Group>
          </FilterField>

          <FilterField>
            <BootstrapForm.Group>
              <BootstrapForm.Label>End date</BootstrapForm.Label>
              <BootstrapForm.Control
                type="date"
                size="sm"
                value={pendingFilters.dateTo ?? ''}
                onChange={(e) => handlePendingFilterChange('dateTo', e.target.value)}
              />
            </BootstrapForm.Group>
          </FilterField>

          <FilterField>
            <BootstrapForm.Group>
              <BootstrapForm.Label>Filter by</BootstrapForm.Label>
              <BootstrapForm.Select
                size="sm"
                className="tw-w-full"
                value={pendingFilters.dateFilterType ?? 'reportedOn'}
                onChange={(e) => handleDateFilterTypeChange(e.target.value)}
              >
                {DATE_FILTER_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </BootstrapForm.Select>
            </BootstrapForm.Group>
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<ValueLabelOption>
              options={PRIORITY_OPTIONS}
              value={csvToValues(pendingFilters.priority)}
              onChange={(values) => handleMultiFilterChange('priority', values)}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="All priorities"
              className="tw-w-full"
              label="Priority"
              noOptionsMessage="No priorities available"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<WorkIdFilterOptions['reporters'][0]>
              options={filterOptions.reporters}
              value={csvToValues(pendingFilters.reportedById)}
              onChange={(values) => handleMultiFilterChange('reportedById', values)}
              getValue={(u) => u.id.toString()}
              formatOption={(u) =>
                [u.name, u.lastName].filter(Boolean).join(' ').trim() || `User #${u.id}`
              }
              placeholder="All reporters"
              className="tw-w-full"
              label="Reported by"
              noOptionsMessage="No reporters found"
            />
          </FilterField>

          <FilterField>
            <MultiSelectDropdown<SourceDropdownRow>
              options={sourceOptions}
              value={csvToValues(pendingFilters.workSource)}
              onChange={(values) => handleMultiFilterChange('workSource', values)}
              getValue={(o) => o.key}
              formatOption={(o) => o.label}
              placeholder="All sources"
              className="tw-w-full"
              label="Source"
              noOptionsMessage="No sources found"
            />
          </FilterField>
        </FilterGrid>
      </BootstrapForm>
    </FiltersCard>
  );
};

export default WorkIdFilters;
